import { useQuery } from '@tanstack/react-query';
import { getCase, getCaseBusinessView } from '../api/cases.ts';
import { ApiError } from '../api/client.ts';
import { useTokenStore } from '../api/use-api.ts';

/**
 * A case read, and the business view (plan §8.1) for an owner or admin the case read refuses
 * because they hold no case row. The one home of that fallback: the case page, the members page
 * and the nav each wrote it, and the agreement screen had none, so the case owner could not open
 * their own client's agreement (TF-0037).
 *
 * `businessFallback` is the caller's own extra condition — the business view is an audited read,
 * so the nav asks it only of someone who could use its answer.
 */
export function useCaseRead(caseId: string | null, { businessFallback }: { readonly businessFallback: boolean }) {
  const store = useTokenStore();
  const caseQ = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId!, store),
    enabled: caseId !== null,
    retry: false,
  });
  const caseAccessDenied =
    caseQ.isError && caseQ.error instanceof ApiError && caseQ.error.code === 'CASE_ACCESS_DENIED';
  const businessQ = useQuery({
    queryKey: ['case-business', caseId],
    queryFn: () => getCaseBusinessView(caseId!, store),
    enabled: caseAccessDenied && businessFallback,
    retry: false,
  });
  return { caseQ, caseAccessDenied, businessQ };
}
